import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../../services/api';
import './Login.css';
import { APP_NAME } from '../../config';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimer, setBlockTimer] = useState(0);
  const [capsLockOn, setCapsLockOn] = useState(false);
  
  // OTP sistemi için yeni state'ler
  const [step, setStep] = useState('login'); // 'login', 'otp', 'telegram'
  const [tempToken, setTempToken] = useState('');
  const [telegramInfo, setTelegramInfo] = useState({
    username: '',
    connectionCode: '',
    botUsername: 'dot_autograms_bot'
  });
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // OTP otomatik doğrulama için ref
  const isVerifyingOTP = useRef(false);

  // Component mount olduğunda remember me kontrolü
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    // Auto-focus için küçük gecikme
    const timer = setTimeout(() => {
      if (!savedUsername) {
        document.getElementById('username-input')?.focus();
      } else {
        document.getElementById('password-input')?.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Block timer countdown
  useEffect(() => {
    let interval;
    if (isBlocked && blockTimer > 0) {
      interval = setInterval(() => {
        setBlockTimer((prev) => {
          if (prev <= 1) {
            setIsBlocked(false);
            setLoginAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isBlocked, blockTimer]);

  // OTP zamanlayıcı
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (step === 'otp' && otpTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [otpTimer, step]);

  // OTP ve Telegram step'lerine geçildiğinde auto-focus için useEffect
  useEffect(() => {
    if (step === 'otp') {
      // OTP input'una otomatik focus
      const timer = setTimeout(() => {
        const otpInput = document.getElementById('otp-input');
        if (otpInput) {
          otpInput.focus();
        }
      }, 300);
      return () => clearTimeout(timer);
    } else if (step === 'telegram') {
      // Telegram bot linkine otomatik focus
      const timer = setTimeout(() => {
        const botLink = document.querySelector('.telegram-bot-link');
        if (botLink) {
          botLink.focus();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // OTP kodu 6 haneli olduğunda otomatik doğrulama
  useEffect(() => {
    if (otpCode.length === 6 && !isVerifyingOTP.current && !loading) {
      verifyOTPCode(otpCode);
    }
  }, [otpCode]);

  // Caps Lock kontrolü
  const checkCapsLock = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  // Form validation
  const validateForm = () => {
    if (!username.trim()) {
      setError('Kullanıcı adı boş olamaz');
      return false;
    }
    if (!password) {
      setError('Şifre boş olamaz');
      return false;
    }
    if (username.length < 3) {
      setError('Kullanıcı adı en az 3 karakter olmalı');
      return false;
    }
    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalı');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Eğer bloklanmışsa işlem yapma
    if (isBlocked) {
      setError(`Çok fazla hatalı deneme. ${blockTimer} saniye bekleyin.`);
      return;
    }

    // Form validation
    if (!validateForm()) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      // OTP sistemli login-request endpoint'ini kullan
      const response = await authAPI.loginRequest(username.trim(), password);

      if (response.data.success) {
        // Remember me özelliği
        if (rememberMe) {
          localStorage.setItem('rememberedUsername', username);
        } else {
          localStorage.removeItem('rememberedUsername');
        }

        if (response.data.requiresOTP) {
          // OTP gönderildi, OTP giriş ekranına geç
          setStep('otp');
          setTempToken(response.data.tempToken);
          setTelegramInfo({
            username: response.data.telegram_username,
            connectionCode: '',
            botUsername: response.data.botUsername || 'dot_autograms_bot'
          });
          setOtpTimer(300); // 5 dakika
          setCanResend(false);
          setError('');
        } else if (response.data.token) {
          // OTP olmadan giriş (dev mode veya OTP devre dışı)
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          setTimeout(() => {
            onLogin(response.data.user, response.data.token);
          }, 500);
        }
      } else if (response.data.requiresTelegram) {
        // Telegram bağlantısı gerekli
        setStep('telegram');
        setTempToken(response.data.tempToken);
        setTelegramInfo({
          username: '',
          connectionCode: response.data.connectionCode,
          botUsername: response.data.botUsername || 'dot_autograms_bot'
        });
        setError('');
      } else {
        setError(response.data.error || 'Giriş başarısız');
      }
    } catch (err) {
      console.error('Login hatası:', err);
      
      // Eğer login-request endpoint'i yoksa legacy login'i dene
      if (err.response?.status === 404) {
        try {
          const legacyResponse = await authAPI.login(username.trim(), password);
          
          if (legacyResponse.data.success) {
            localStorage.setItem('token', legacyResponse.data.token);
            localStorage.setItem('user', JSON.stringify(legacyResponse.data.user));
            
            setTimeout(() => {
              onLogin(legacyResponse.data.user, legacyResponse.data.token);
            }, 500);
            return;
          }
        } catch (legacyErr) {
          handleLoginError(legacyErr);
        }
      } else {
        handleLoginError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginError = (err) => {
    // Login attempt sayısını artır
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);
    
    // 5 denemeden sonra blokla
    if (newAttempts >= 5) {
      setIsBlocked(true);
      setBlockTimer(60);
      setError('Çok fazla hatalı deneme. 1 dakika bekleyin.');
      setPassword('');
      return;
    }
    
    // Hata mesajlarını yönet
    if (err.code === 'ECONNABORTED') {
      setError('Bağlantı zaman aşımına uğradı. Lütfen tekrar deneyin.');
    } else if (err.response?.status === 429) {
      const retryAfter = err.response.data?.retryAfter || 60;
      setIsBlocked(true);
      setBlockTimer(retryAfter);
      setError(`Çok fazla deneme. ${retryAfter} saniye bekleyin.`);
    } else if (err.response?.status === 401) {
      setError(`Geçersiz kullanıcı adı veya şifre (${5 - newAttempts} deneme hakkı kaldı)`);
    } else if (err.response?.status === 403) {
      setError('Bu hesap devre dışı bırakılmış. Yönetici ile iletişime geçin.');
    } else if (err.response?.data?.error) {
      setError(err.response.data.error);
    } else if (err.message === 'Network Error') {
      setError('Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edin.');
    } else {
      setError('Giriş başarısız. Lütfen tekrar deneyin.');
    }
    
    // Şifreyi temizle
    setPassword('');
    
    // Hata durumunda input'a focus
    setTimeout(() => {
      document.getElementById('password-input')?.focus();
    }, 100);
  };

  const verifyOTPCode = async (code) => {
    if (code.length !== 6) {
      return;
    }

    // Çift doğrulama önleme
    if (isVerifyingOTP.current) {
      return;
    }

    isVerifyingOTP.current = true;
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.verifyOTP(tempToken, code);

      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        setTimeout(() => {
          onLogin(response.data.user, response.data.token);
        }, 500);
      } else {
        setError(response.data.error || 'OTP doğrulama başarısız');
        setOtpCode('');
        isVerifyingOTP.current = false;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'OTP doğrulama hatası');
      setOtpCode('');
      isVerifyingOTP.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setLoading(true);
    setError('');

    try {
      const response = await authAPI.resendOTP(tempToken);

      if (response.data.success) {
        setTempToken(response.data.tempToken);
        setOtpTimer(300); // 5 dakika
        setCanResend(false);
        setOtpCode('');
        setError('');
        isVerifyingOTP.current = false;
        // Başarı mesajı göster
        setError('✅ Yeni OTP kodu Telegram\'a gönderildi');
        setTimeout(() => setError(''), 3000);
      } else {
        setError(response.data.error || 'OTP gönderim hatası');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Enter tuşu ile submit
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && !isBlocked) {
      if (step === 'login') {
        handleSubmit(e);
      }
    }
  };

  // Şifre göster/gizle
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Back button handler
  const handleBack = () => {
    setStep('login');
    setOtpCode('');
    setError('');
    setPassword('');
    setOtpTimer(0);
    setCanResend(false);
    isVerifyingOTP.current = false;
  };

  // OTP Ekranı
  if (step === 'otp') {
    return (
      <div className="login-container">
        <div className="login-particles"></div>
        
        <div className={`login-box ${loading ? 'loading' : ''}`}>
          <div className="login-header">
            <div className="logo-animation">
              <span className="logo-icon">🔐</span>
            </div>
            <h1>OTP Doğrulama</h1>
            <p>Telegram'a gönderilen kodu girin</p>
            {telegramInfo.username && (
              <div className="telegram-badge">@{telegramInfo.username}</div>
            )}
          </div>
          
          <div className="login-form">
            <div className="form-group otp-group">
              <label htmlFor="otp-input">
                <i className="icon">🔢</i> Doğrulama Kodu
              </label>
              <input
                id="otp-input"
                type="text"
                value={otpCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpCode(value);
                  setError('');
                }}
                placeholder="000000"
                maxLength="6"
                autoComplete="off"
                className={`otp-input ${otpCode.length > 0 ? 'has-value' : ''}`}
                disabled={loading || (otpTimer === 0 && !canResend)}
              />
              <div className="otp-timer-info">
                {otpTimer > 0 ? (
                  <span className="timer-active">⏱️ Kod geçerlilik süresi: {formatTime(otpTimer)}</span>
                ) : (
                  <span className="timer-expired">⚠️ Kod süresi doldu</span>
                )}
              </div>
              {loading && otpCode.length === 6 && (
                <div className="otp-verifying">
                  <span className="spinner"></span>
                  <span>Doğrulanıyor...</span>
                </div>
              )}
            </div>
            
            {error && (
              <div className={`error-message ${error.includes('✅') ? 'success' : ''} show`}>
                <span className="error-icon">{error.includes('✅') ? '' : '⚠️'}</span>
                <span className="error-text">{error.replace('✅ ', '')}</span>
              </div>
            )}

            {canResend && (
              <button
                type="button"
                onClick={handleResendOTP}
                className="resend-otp-button"
                disabled={loading}
              >
                📨 Yeni Kod Gönder
              </button>
            )}

            <button
              type="button"
              onClick={handleBack}
              className="back-to-login"
            >
              ← Giriş Ekranına Dön
            </button>
          </div>
          
          <div className="login-footer">
            <div className="divider"></div>
            <div className="footer-info">
              <span className="version">v1.0.0 OTP</span>
              <span className="separator">•</span>
              <span className="status">
                <span className={`status-dot ${loading ? 'connecting' : 'online'}`}></span>
                {loading ? 'Doğrulanıyor' : 'Hazır'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Telegram Bağlama Ekranı
  if (step === 'telegram') {
    return (
      <div className="login-container">
        <div className="login-particles"></div>
        
        <div className="login-box">
          <div className="login-header">
            <div className="logo-animation">
              <span className="logo-icon">📱</span>
            </div>
            <h1>Telegram Bağla</h1>
            <p>OTP almak için Telegram hesabınızı bağlayın</p>
          </div>
          
          <div className="telegram-steps">
            <div className="telegram-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <p>Telegram'da bot'u açın:</p>
                <a 
                  href={`https://t.me/${telegramInfo.botUsername}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="telegram-bot-link"
                >
                  <span className="telegram-icon">✈️</span>
                  @{telegramInfo.botUsername}
                </a>
              </div>
            </div>

            <div className="telegram-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <p>Bot'a şu komutu gönderin:</p>
                <div className="connection-code-box">
                  <code>/connect {telegramInfo.connectionCode}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`/connect ${telegramInfo.connectionCode}`);
                      setError('✅ Kod kopyalandı!');
                      setTimeout(() => setError(''), 2000);
                    }}
                    className="copy-code-btn"
                  >
                    📋
                  </button>
                </div>
              </div>
            </div>

            <div className="telegram-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <p>Bot'tan onay mesajı aldıktan sonra tekrar giriş yapın</p>
              </div>
            </div>
          </div>

          {error && (
            <div className={`error-message ${error.includes('✅') ? 'success' : ''} show`}>
              <span className="error-text">{error.replace('✅ ', '')}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleBack}
            className="back-to-login full-width"
          >
            ← Giriş Ekranına Dön
          </button>
          
          <div className="login-footer">
            <div className="divider"></div>
            <div className="footer-info">
              <span className="version">v1.0.0 OTP</span>
              <span className="separator">•</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login Ekranı
  return (
    <div className="login-container">
      <div className="login-particles"></div>
      
      <div className={`login-box ${loading ? 'loading' : ''}`}>
        <div className="login-header">
          <div className="logo-animation">
            <span className="logo-icon">🚀</span>
          </div>
          <h1>{window.location.hostname.includes('tgsosmdya') ? 'TG Sosmedya' : 'Autogram'}</h1>
          <p>Yönetim Paneli</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username-input">
              <i className="icon">👤</i> Kullanıcı Adı
            </label>
            <input
              id="username-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Kullanıcı adınızı girin"
              required
              disabled={loading || isBlocked}
              autoComplete="username"
              className={username.length > 0 ? 'has-value' : ''}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password-input">
              <i className="icon">🔒</i> Şifre
            </label>
            <div className="password-input-wrapper">
              <input
                id="password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                onKeyDown={checkCapsLock}
                onKeyUp={checkCapsLock}
                placeholder="Şifrenizi girin"
                required
                disabled={loading || isBlocked}
                autoComplete="current-password"
                className={password.length > 0 ? 'has-value' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={loading || isBlocked}
                tabIndex="-1"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {capsLockOn && (
              <div className="caps-lock-warning">
                ⚠️ Caps Lock açık
              </div>
            )}
          </div>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading || isBlocked}
              />
              <span className="checkbox-custom"></span>
              Beni hatırla
            </label>
          </div>
          
          {error && (
            <div className={`error-message ${error ? 'show' : ''}`}>
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}
          
          {isBlocked && blockTimer > 0 && (
            <div className="block-timer">
              <div className="timer-circle">
                <svg>
                  <circle
                    cx="20"
                    cy="20"
                    r="18"
                    style={{
                      strokeDasharray: `${(blockTimer / 60) * 113} 113`
                    }}
                  />
                </svg>
                <span className="timer-text">{blockTimer}</span>
              </div>
              <span className="timer-message">saniye bekleyin</span>
            </div>
          )}
          
          <button 
            type="submit" 
            className={`login-button ${loading ? 'loading' : ''} ${isBlocked ? 'blocked' : ''}`}
            disabled={loading || isBlocked}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                <span>Giriş yapılıyor...</span>
              </>
            ) : isBlocked ? (
              <span>Bekleyin ({blockTimer}s)</span>
            ) : (
              <>
                <span>Giriş Yap</span>
                <span className="arrow">→</span>
              </>
            )}
          </button>
        </form>
        
        <div className="login-footer">
          <div className="divider"></div>
          
          <div className="footer-info">
            <span className="version">v1.0.0</span>
            <span className="separator">•</span>
            <span className="status">
              <span className={`status-dot ${loading ? 'connecting' : 'online'}`}></span>
              {loading ? 'Bağlanıyor' : 'Çevrimiçi'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;